/**
 * Filing API Service
 *
 * API client for the filing history system.
 * Endpoints are registered under /api/v1/filings prefix.
 *
 * Session 3.2: Filing History Frontend
 */

import apiClient, { handleApiResponse } from './client';
import type { ApiResponse } from '@/types';
import type {
  FilingListResponse,
  FilingDetail,
  FilingStats,
  FilingListParams,
  UpdateNotesRequest,
} from '@/types/filing';

// =============================================================================
// API RESPONSE WRAPPERS
// =============================================================================

interface YearsResponse {
  years: number[];
}

interface MarkAsFiledResponse {
  id: string;
  status: string;
  filed_at: string;
}

interface UpdateNotesResponse {
  id: string;
  notes: string;
}

// =============================================================================
// FILING API
// =============================================================================

export const filingApi = {
  // ===========================================================================
  // LIST OPERATIONS
  // ===========================================================================

  /**
   * Get paginated list of filings.
   *
   * GET /api/v1/filings?page=1&per_page=20&year=2025
   *
   * @param params - Query parameters (year, page, per_page)
   * @returns Paginated filing list with summaries
   */
  list: async (params: FilingListParams = {}): Promise<FilingListResponse> => {
    const response = await apiClient.get<ApiResponse<FilingListResponse>>(
      '/v1/filings',
      { params }
    );
    return handleApiResponse(response);
  },

  /**
   * Get list of years with filings for filter dropdown.
   *
   * GET /api/v1/filings/years
   *
   * @returns Array of years in descending order
   */
  getYears: async (): Promise<number[]> => {
    const response =
      await apiClient.get<ApiResponse<YearsResponse>>('/v1/filings/years');
    return handleApiResponse(response).years;
  },

  /**
   * Get filing statistics for the user.
   *
   * GET /api/v1/filings/stats
   *
   * @returns Filing statistics
   */
  getStats: async (): Promise<FilingStats> => {
    const response =
      await apiClient.get<ApiResponse<FilingStats>>('/v1/filings/stats');
    return handleApiResponse(response);
  },

  // ===========================================================================
  // DETAIL OPERATIONS
  // ===========================================================================

  /**
   * Get filing detail with employees.
   *
   * GET /api/v1/filings/{id}
   *
   * @param id - Filing UUID
   * @returns Filing detail with employee list
   */
  getDetail: async (id: string): Promise<FilingDetail> => {
    const response = await apiClient.get<ApiResponse<FilingDetail>>(
      `/v1/filings/${id}`
    );
    return handleApiResponse(response);
  },

  // ===========================================================================
  // DOWNLOAD OPERATIONS
  // ===========================================================================

  /**
   * Download C10 file for a filing.
   *
   * GET /api/v1/filings/{id}/download
   *
   * Triggers browser file download with Content-Disposition header.
   *
   * @param id - Filing UUID
   * @param filename - Filename for download (e.g., "C10_January_2025.txt")
   */
  download: async (id: string, filename: string): Promise<void> => {
    // Use fetch directly for blob download since axios interceptor modifies response
    const token = localStorage.getItem('access_token');
    const baseUrl = import.meta.env.VITE_API_URL || '/api';

    const response = await fetch(`${baseUrl}/v1/filings/${id}/download`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // ===========================================================================
  // UPDATE OPERATIONS
  // ===========================================================================

  /**
   * Mark a filing as submitted to NIB.
   *
   * POST /api/v1/filings/{id}/mark-filed
   *
   * @param id - Filing UUID
   * @returns Updated filing data
   */
  markAsFiled: async (id: string): Promise<MarkAsFiledResponse> => {
    const response = await apiClient.post<ApiResponse<MarkAsFiledResponse>>(
      `/v1/filings/${id}/mark-filed`
    );
    return handleApiResponse(response);
  },

  /**
   * Update filing notes.
   *
   * PATCH /api/v1/filings/{id}/notes
   *
   * @param id - Filing UUID
   * @param notes - New notes content
   * @returns Updated notes
   */
  updateNotes: async (
    id: string,
    notes: string
  ): Promise<UpdateNotesResponse> => {
    const data: UpdateNotesRequest = { notes };
    const response = await apiClient.patch<ApiResponse<UpdateNotesResponse>>(
      `/v1/filings/${id}/notes`,
      data
    );
    return handleApiResponse(response);
  },
};

export default filingApi;
